import { cleanSrc } from '@sohanemon/utils';
import { Img as BaseImg, type ImgProps } from '@sohanscript/ui/img';
import { shimmerPlaceholder } from '@sohanscript/ui/img-utils';
import type * as React from 'react';
import { sanityImagePlaceholder } from '@/lib/utils/sanity-image-placeholder';

const SRCSET_WIDTHS = [320, 640, 960, 1280, 1920];

function resizeUrl(url: string, width: number): string {
	const base = url.split('?')[0];
	if (url.includes('cdn.sanity.io'))
		return `${base}?w=${width}&auto=format&q=80`;
	if (url.includes('res.cloudinary.com'))
		return url.replace(/\/upload\//, `/upload/w_${width},f_auto,q_auto/`);
	return url;
}

function buildSrcSet(url: string): string | undefined {
	const resized = SRCSET_WIDTHS.map((w) => `${resizeUrl(url, w)} ${w}w`);

	if (resized[0] === `${url} ${SRCSET_WIDTHS[0]}w`) return undefined;
	return resized.join(', ');
}

export const Img: React.FC<ImgProps> = ({
	src,
	placeholder,
	sizes,
	width,

	...props
}) => {
	const resolved = cleanSrc(src);
	const isSanity = resolved.includes('cdn.sanity.io');

	const thumb = (() => {
		switch (placeholder) {
			case 'none':
				return 'none';
			case 'shimmer':
				return shimmerPlaceholder;
			default:
				return (
					placeholder ??
					(isSanity ? sanityImagePlaceholder(resolved) : shimmerPlaceholder)
				);
		}
	})();

	const srcSet = buildSrcSet(resolved);
	const optimizedSrc = width ? resizeUrl(resolved, width) : resolved;
	const defaultSizes =
		sizes ??
		(width
			? `${width}px`
			: '(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw');

	return (
		<BaseImg
			src={optimizedSrc}
			width={width}
			placeholder={thumb}
			srcSet={srcSet}
			sizes={srcSet ? defaultSizes : undefined}
			{...props}
		/>
	);
};
